/*
 * Shared pieces for the CXOPanel/orderAnalysis pages.
 *
 * CouponCampaignPage — Birthday List / Spin The Wheel. Both old pages had their API calls
 * commented out (Search did nothing), so this keeps the same cards and columns and says the
 * report isn't connected yet. Pass `fetchAction` (e.g. getAddToCartAnalysis) to switch it on.
 */
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import moment from "moment";
import { Copy, Gift, MessageCircle, ShoppingBag, Ticket } from "lucide-react";
import { showSuccessSnackbar } from "../../Action/snackbarAction";
import { getExamTypeSectionList } from "../../Action/adminPanelAction";
import {
  CustomAdminTable,
  CustomDropdowns,
  CustomIconButton,
  CustomPageLayout,
  CustomStatCard,
  CustomSummaryCards,
  WhatsAppLogo,
} from "../../Component/customComponents";
import { BatchNames, TableTitle, formatDateRange, formatINR, openWhatsAppChat, todayISO } from "./transactionCommon";
import { CountLink, exportRowsToExcel } from "./countListCommon";

/** "+91 98765 43210" / 9876543210 → "98******10"; never shows more than 4 digits */
export const maskMobile = (m) => {
  const digits = String(m ?? "").replace(/\D/g, "");
  const local = digits.length > 10 ? digits.slice(-10) : digits; // drop the country code
  return local.length >= 6 ? `${local.slice(0, 2)}******${local.slice(-2)}` : "******";
};
const n = (v) => Number(v) || 0;
const fmt = (v) => n(v).toLocaleString("en-IN");

/** masked mobile + copy button (the old pages' copy icon) */
export const MobileCell = ({ value }) => {
  const dispatch = useDispatch();
  if (!value) return "—";
  return (
    <span className="d-inline-flex align-items-center gap-1">
      {maskMobile(value)}
      <CustomIconButton
        icon={<Copy size={12} />}
        size="sm"
        title="Copy number"
        onClick={() => {
          navigator.clipboard?.writeText(String(value));
          dispatch(showSuccessSnackbar("Number copied!"));
        }}
      />
    </span>
  );
};

const DATE_FIELDS = [
  { key: "startDate", label: "Start Date", type: "date", half: true, isRequired: true, defaultValue: todayISO },
  {
    key: "endDate",
    label: "End Date",
    type: "date",
    half: true,
    isRequired: true,
    defaultValue: todayISO,
    minDate: (v) => v.startDate,
    validate: (value, v) => (value && v.startDate && value < v.startDate ? "End date can't be before the start date" : ""),
  },
];

/**
 * Props: pageTitle, tableId, couponLabel ("Birthday Coupon" | "Spin The Wheel"),
 *        usedLabel, exportName, maskMobileNo, fetchAction? / detailAction? (not wired before)
 */
export const CouponCampaignPage = ({ pageTitle, tableId, couponLabel, usedLabel, exportName, maskMobileNo = true, fetchAction, detailAction }) => {
  const dispatch = useDispatch();
  const [filters, setFilters] = useState(() => ({ startDate: todayISO(), endDate: todayISO() }));
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState(null); // { date, rows }

  const connected = Boolean(fetchAction);

  const fetchRows = (f) => {
    if (!fetchAction) return; // the old Search did nothing (API call commented out)
    setLoading(true);
    dispatch(
      fetchAction(
        { startDate: f.startDate, endDate: f.endDate },
        (res) => {
          setRows(res?.data || []);
          setLoading(false);
        },
        () => {
          setRows([]);
          setLoading(false);
        },
      ),
    );
  };

  const openPayments = (row) => {
    const day = moment.utc(row?.date).format("YYYY-MM-DD");
    setDetail({ date: row?.date, rows: [] });
    if (!detailAction) return;
    dispatch(detailAction({ startDate: day, endDate: day, type: "pendingCart" }, (res) => setDetail({ date: row?.date, rows: res?.data || [] }), () => {}));
  };

  const totals = useMemo(
    () => ({
      coupons: rows.reduce((t, r) => t + n(r?.totalAddToCartCount), 0),
      sent: rows.reduce((t, r) => t + n(r?.totalSentMessages), 0),
      paid: rows.reduce((t, r) => t + n(r?.paymentDoneAfterMessage), 0),
      used: rows.reduce((t, r) => t + n(r?.couponUsedCount), 0),
    }),
    [rows],
  );

  const columns = [
    { key: "date", header: "Date", minWidth: 110, render: (v) => (v ? <strong>{moment.utc(v).format("DD/MM/YYYY")}</strong> : "—") },
    { key: "totalAddToCartCount", header: `Total ${couponLabel}`, align: "right", render: (v) => (v ? fmt(v) : "—") },
    { key: "totalSentMessages", header: "Messages Sent", align: "right", render: fmt },
    { key: "totalDeliveredMessages", header: "Delivered", align: "right", render: fmt },
    { key: "totalReadMessages", header: "Read", align: "right", render: fmt },
    { key: "totalFailedMessages", header: "Failed", align: "right", render: fmt },
    { key: "alreadyMessageSent", header: "Already Message Sent", align: "right", render: fmt },
    { key: "belowPricePayments", header: "Users below ₹1000 Payment", align: "right", minWidth: 150, render: fmt },
    { key: "paymentDoneAfterMessage", header: "Paid After Message", align: "right", minWidth: 130, render: (v, r) => <CountLink value={n(v)} onClick={() => openPayments(r)} title="View payments" /> },
    { key: "couponUsedCount", header: usedLabel, align: "right", minWidth: 130, render: fmt },
    { key: "couponUsedRate", header: "Coupon Conversion", align: "right", render: (v) => `${v ? Number(v).toFixed(2) : 0}%` },
  ];

  const detailColumns = [
    { key: "firstName", header: "First Name", render: (v) => <strong>{v || "—"}</strong> },
    { key: "lastName", header: "Last Name" },
    { key: "mobileNo", header: "Mobile No", minWidth: 140, render: (v) => (maskMobileNo ? <MobileCell value={v} /> : v || "—") },
    { key: "orderNo", header: "Order No" },
    { key: "orderDate", header: "Order Date", render: (v) => (v ? moment.utc(v).format("DD/MM/YYYY") : "—") },
    { key: "coupons", header: "Coupon Name", minWidth: 160, wrap: true, render: (v) => ((v || []).length ? v.map((c, i) => <div key={i}>{c}</div>) : "—") },
  ];

  const onExport = () =>
    exportRowsToExcel(
      exportName,
      "Sheet1",
      rows.map((r) => ({ Date: r?.date ? moment.utc(r.date).format("DD-MM-YYYY") : "", ...Object.fromEntries(columns.slice(1).map((c) => [c.header, n(r?.[c.key])])) })),
    );

  const notConnected = "This report isn't connected to the server yet, so there's no data to show.";

  return (
    <CustomPageLayout title={detail ? "PAYMENT DONE AFTER MESSAGE" : pageTitle} isGoBack={Boolean(detail)} onBack={() => setDetail(null)}>
      {detail ? (
        <CustomAdminTable
          key="detail"
          id={`${tableId}-payments`}
          title={`Payments after message · ${moment.utc(detail.date).format("DD/MM/YYYY")} (${detail.rows.length})`}
          columns={detailColumns}
          data={detail.rows}
          rowKey={(r, i) => r?._id || i}
          enableSerialNumber
          serialHeader="S.No"
          emptyTitle="No payments"
          emptyDescription={detailAction ? "Nothing for this day." : notConnected}
        />
      ) : (
        <div className="d-flex flex-column gap-3">
          <div className="row g-3">
            <CustomStatCard colClass="col-lg-3 col-sm-6 col-12" title={`Total ${couponLabel}`} value={fmt(totals.coupons)} icon={<Gift size={18} />} tone="primary" loading={loading} />
            <CustomStatCard colClass="col-lg-3 col-sm-6 col-12" title="Total Message Sent" value={fmt(totals.sent)} icon={<MessageCircle size={18} />} tone="info" loading={loading} />
            <CustomStatCard colClass="col-lg-3 col-sm-6 col-12" title="After Message Payment Done" value={fmt(totals.paid)} icon={<ShoppingBag size={18} />} tone="success" loading={loading} />
            <CustomStatCard colClass="col-lg-3 col-sm-6 col-12" title={usedLabel} value={fmt(totals.used)} icon={<Ticket size={18} />} tone="warning" loading={loading} />
          </div>
          <CustomAdminTable
            key="days"
            id={tableId}
            title={<TableTitle title={`Day wise (${rows.length})`} from={filters.startDate} to={filters.endDate} />}
            columns={columns}
            data={rows}
            loading={loading}
            rowKey={(r, i) => r?.date || i}
            enableSerialNumber
            serialHeader="S.No"
            exportButton={rows.length > 0}
            onExport={onExport}
            advancedFilters={DATE_FIELDS}
            advancedFilterValues={filters}
            onApplyFilters={(values) => {
              setFilters(values);
              fetchRows(values);
            }}
            applyFiltersOnReset
            filterSubtitle="Pick the dates"
            emptyTitle={connected ? "No records" : "Not connected yet"}
            emptyDescription={connected ? "Nothing for this period." : notConnected}
            maxHeight="70vh"
          />
        </div>
      )}
    </CustomPageLayout>
  );
};

/* =====================================================================
 * OrderDayPage — the day-wise order reports (Add On, Cancelled, Upgrade, Cross Selling, …).
 * Every one of them: dates (+ optional section / sub section) → one row per day with a Total row,
 * summary cards, and counts that open the users behind them (with WhatsApp chat).
 * ===================================================================== */

/** WhatsApp chat button for a user row */
export const WhatsAppChatButton = ({ mobileNo }) => (
  <CustomIconButton icon={<WhatsAppLogo />} tone="whatsapp" size="sm" title="Chat on WhatsApp" onClick={() => openWhatsAppChat(mobileNo)} disabled={!mobileNo} />
);

/** chip colour by what the number means */
export const chipTone = (label = "") =>
  /fail|invalid|reject|cancel/i.test(label) ? "danger" : /not generated|pending|expir/i.test(label) ? "warning" : /paid|payment|admission|upgrade|conversion/i.test(label) ? "success" : "primary";

const pctText = (v, decimals) => `${decimals != null ? (Number(v) || 0).toFixed(decimals) : v || 0}%`;

/** default user-list columns (the old detail tables) */
export const USER_LIST_COLUMNS = {
  firstName: { key: "firstName", header: "First Name", minWidth: 120, render: (v) => <strong>{v || "—"}</strong> },
  lastName: { key: "lastName", header: "Last Name", minWidth: 110 },
  mobileNo: { key: "mobileNo", header: "Mobile No", minWidth: 140, render: (v) => <MobileCell value={v} /> },
  orderNo: { key: "orderNo", header: "Order No", minWidth: 110 },
  orderDate: { key: "orderDate", header: "Order Date", minWidth: 110, render: (v) => (v ? moment.utc(v).format("DD/MM/YYYY") : "—") },
  couponName: {
    key: "couponName",
    header: "Coupon Name",
    minWidth: 140,
    wrap: true,
    render: (v) => (Array.isArray(v) ? (v.length ? v.map((c, i) => <div key={i}>{c}</div>) : "—") : v || "—"),
  },
  // lead lists: the API spells it "firtName" in some responses
  leadFirstName: { key: "firtName", header: "First Name", minWidth: 120, render: (v, r) => <strong>{v || r?.firstName || "—"}</strong> },
  examName: { key: "examName", header: "Exam Name", minWidth: 120 },
  baseCourseName: { key: "baseCourseName", header: "Exam Stream", minWidth: 130, render: (v) => <BatchNames names={v} /> },
  coupons: { key: "coupons", header: "Coupon Name", minWidth: 140, wrap: true, render: (v) => ((v || []).length ? v.map((c, i) => <div key={i}>{c}</div>) : "—") },
  reason: { key: "reason", header: "Reason", minWidth: 180, wrap: true, render: (v) => (v ? <span className="text-danger">{v}</span> : "—") },
  examTypeId: { key: "examTypeId", header: "Exam Name", minWidth: 120 },
  baseCourseId: { key: "baseCourseId", header: "Exam Stream", minWidth: 130, render: (v) => <BatchNames names={v} /> },
  source: { key: "source", header: "Source", minWidth: 110 },
  status: { key: "status", header: "Status", minWidth: 100 },
  whatsapp: { key: "whatsapp", header: "Action", align: "center", render: (_, r) => <WhatsAppChatButton mobileNo={r?.mobileNo} /> },
};
export const userListColumns = (keys) => keys.map((k) => (typeof k === "string" ? USER_LIST_COLUMNS[k] : k));

const SECTION_FIELDS = (sections) => [
  { key: "section", label: "Section Name", type: "dropdown", options: sections, clears: ["subSection"] },
  {
    key: "subSection",
    label: "Sub Section",
    type: "dropdown",
    options: (v) => v.section?.subSection || [],
    disabled: (v) => !v.section,
    hint: (v) => (!v.section ? "Select a section first" : undefined),
  },
];

/* ---------- grouped summary cards (CustomSummaryCard) ---------- */

/** 1204 → "1,204" (missing → "0") */
/**
 * A summary value as a number: numbers and numeric strings ("1,204", "4.76%", " 12 ") count;
 * null / undefined / "" / NaN / Infinity / booleans / objects / arrays → 0 (shown as 0 or 0%).
 */
const summaryNumber = (v) => {
  const x = typeof v === "number" ? v : typeof v === "string" ? Number(v.replace(/[,%\s]/g, "")) : NaN;
  return Number.isFinite(x) ? x || 0 : 0; // `|| 0` turns -0 into 0
};
export const countOf = (v) => summaryNumber(v).toLocaleString("en-IN");
/** 4.7619 / "4.76" → "4.76%", 0 → "0%" (at most 2 decimals, no trailing zeros) */
export const pctOf = (v) => `${+summaryNumber(v).toFixed(2) || 0}%`;
/** the "Default / Counsellor / Publication …" card: Payments | Conversion */
export const paymentGroup = (title, tone, payments, conversion) => ({
  title,
  tone,
  metrics: [
    { label: "Payments", value: countOf(payments) },
    { label: "Conversion", value: pctOf(conversion) },
  ],
});

/**
 * Props
 *  - pageTitle, tableId, exportName?
 *  - fetchAction(body, cb, errCb)          request is { startDate, endDate, [sectionKey]?, [subSectionKey]? }
 *  - parse(res) → { rows, summary }
 *  - sectionKeys  { section: "examSectionId", subSection: "examSubSectionId" } | null (no section filter)
 *  - dateKey      the row's date field ("date" | "orderDate" …)
 *  - columns      [{ key, header, kind: "count" | "pct" | "money" | "list" | "text", listTitle?, drill?, decimals?, userColumns?,
 *                    total?: "sum" | "summary" | "none" | (rows, summary) => value }]
 *  - summaryGroups(summary, rows) → [{ title, tone, metrics: [{ label, value }] }]  the grouped cards on top
 *    (CustomSummaryCard: e.g. Total → Registration | Paid Users | Day Conversion)
 *  - userColumns  keys of USER_LIST_COLUMNS (or full column objects) for the drill-down list
 *  - extraBody(filters) → extra request keys; extraFields → extra drawer fields; defaultFilters; onFilterChange
 *  - column.get(row) reads a nested value (e.g. row.stageOne.totalMessageCount)
 *  - column.fetchList(row, filters, dispatch) → Promise<rows>: fetch the users instead of reading row[column.key]
 *    (onOpenList does the same for every column)
 *  - column.listFilter { label, options | () => options, match(user, option) } — a dropdown over that user list
 *    (or refetch(row, option, dispatch) → Promise<rows> to filter on the server)
 */
export const OrderDayPage = ({
  pageTitle,
  tableId,
  exportName,
  fetchAction,
  parse,
  sectionKeys = { section: "examSectionId", subSection: "examSubSectionId" },
  dateKey = "date",
  columns: colDefs,
  summaryGroups,
  userColumns =["firstName", "lastName", "mobileNo", "orderNo", "orderDate", "whatsapp"],
  extraBody,
  extraFields = [],
  defaultFilters = {},
  onOpenList,
  onFilterChange,
}) => {
  const dispatch = useDispatch();
  const [sections, setSections] = useState([]);
  const [filters, setFilters] = useState(() => ({ startDate: todayISO(), endDate: todayISO(), section: "", subSection: "", ...defaultFilters }));
  const [data, setData] = useState({ rows: [], summary: {} });
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState(null); // { title, date, rows, loading }
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const buildBody = (f) => ({
    startDate: f.startDate,
    endDate: f.endDate,
    ...(sectionKeys && f.section?._id && { [sectionKeys.section]: f.section._id }),
    ...(sectionKeys && f.subSection?._id && { [sectionKeys.subSection]: f.subSection._id }),
    ...(extraBody ? extraBody(f) : {}),
  });

  const fetchRows = (f) => {
    setLoading(true);
    dispatch(
      fetchAction(
        buildBody(f),
        (res) => {
          const parsed = parse(res) || {};
          setData({ rows: Array.isArray(parsed.rows) ? parsed.rows : [], summary: parsed.summary || {} });
          setLoading(false);
        },
        () => {
          setData({ rows: [], summary: {} });
          setLoading(false);
        },
      ),
    );
  };

  useEffect(() => {
    if (sectionKeys) dispatch(getExamTypeSectionList((res) => setSections(res || [])));
    fetchRows(filters); // today on open, as before
  }, []);

  const { rows, summary } = data;

  const openList = (row, col) => {
    const title = col.listTitle || col.header;
    setPage(1);
    const fetchList = col.fetchList || onOpenList;
    if (fetchList) {
      setDetail({ title, date: row?.[dateKey], row, rows: [], loading: true, userColumns: col.userColumns, listFilter: col.listFilter, filterValue: "" });
      Promise.resolve(fetchList(row, filters, dispatch, col))
        .then((list) => setDetail((d) => d && { ...d, rows: list || [], loading: false }))
        .catch(() => setDetail((d) => d && { ...d, loading: false }));
    } else {
      setDetail({ title, date: row?.[dateKey], rows: row?.[col.key] || [], loading: false, userColumns: col.userColumns, listFilter: col.listFilter, filterValue: "" });
    }
  };

  const valueOf = (row, col) => (col.get ? col.get(row) : col.kind === "list" ? (row?.[col.key] || []).length : row?.[col.key]);
  const show = (col, v) => {
    if (col.kind === "pct") return pctText(v, col.decimals);
    if (col.kind === "money") return formatINR(Number(v) || 0);
    if (col.kind === "text") return v || "—";
    return (Number(v) || 0).toLocaleString("en-IN");
  };

  // the old tables' first "Total" row
  const totalRow = useMemo(() => {
    if (!rows.length) return null;
    const t = { _total: true };
    colDefs.forEach((c) => {
      const mode = c.total || (c.kind === "pct" ? "summary" : c.kind === "text" ? "none" : "sum");
      if (typeof mode === "function") t[c.key] = mode(rows, summary);
      else if (mode === "summary") t[c.key] = summary?.[c.key];
      else if (mode === "none") t[c.key] = "";
      else t[c.key] = rows.reduce((s, r) => s + (Number(valueOf(r, c)) || 0), 0);
    });
    return t;
  }, [rows, summary]);

  const columns = [
    { key: dateKey, header: "Date", minWidth: 110, render: (v, r) => (r._total ? <strong>Total</strong> : v ? moment.utc(new Date(v)).format("DD/MM/YYYY") : "—") },
    ...colDefs.map((c) => ({
      key: c.key,
      header: c.header,
      // centred so a clickable chip and the plain Total value line up
      align: c.kind === "text" ? "left" : "center",
      minWidth: c.minWidth || 110,
      render: (_, r) => {
        if (r._total) {
          if (c.kind === "text" || r[c.key] === "") return "";
          return <strong>{show({ ...c, kind: c.kind === "list" ? "count" : c.kind }, r[c.key])}</strong>;
        }
        if (c.kind === "list" || c.drill || c.fetchList) return <CountLink value={valueOf(r, c)} onClick={() => openList(r, c)} title={`View ${c.listTitle || c.header}`} tone={c.tone || chipTone(c.header)} />;
        return show(c, c.get ? c.get(r) : r?.[c.key]);
      },
    })),
  ];

  const tableRows = totalRow ? [totalRow, ...rows] : [];
  const filterFields = useMemo(
    () => [
      { key: "startDate", label: "Start Date", type: "date", half: true, isRequired: true, defaultValue: () => defaultFilters.startDate || todayISO(), maxDate: () => todayISO() },
      {
        key: "endDate",
        label: "End Date",
        type: "date",
        half: true,
        isRequired: true,
        defaultValue: todayISO,
        minDate: (v) => v.startDate,
        maxDate: () => todayISO(),
        validate: (value, v) => (value && v.startDate && value < v.startDate ? "End date can't be before the start date" : ""),
      },
      ...(sectionKeys ? SECTION_FIELDS(sections) : []),
      ...extraFields,
    ],
    [sections, extraFields],
  );

  const onExport = () =>
    exportRowsToExcel(
      exportName || pageTitle,
      "Sheet1",
      tableRows.map((r) => ({
        Date: r._total ? "Total" : r[dateKey] ? moment.utc(new Date(r[dateKey])).format("DD-MM-YYYY") : "",
        ...Object.fromEntries(colDefs.map((c) => [c.header, r._total ? r[c.key] ?? "" : c.kind === "pct" ? pctText(r?.[c.key], c.decimals) : valueOf(r, c) ?? ""])),
      })),
    );

  const groups = summaryGroups ? summaryGroups(summary || {}, rows) : [];
  const lf = detail?.listFilter;
  const detailRows = (detail?.rows || []).filter((u) => !lf || lf.refetch || !detail.filterValue || lf.match(u, detail.filterValue));

  return (
    <CustomPageLayout title={detail ? detail.title : pageTitle} isGoBack={Boolean(detail)} onBack={() => setDetail(null)}>
      {detail ? (
        <CustomAdminTable
          key="detail"
          id={`${tableId}-users`}
          title={`${detail.title}${detail.date ? ` · ${moment.utc(new Date(detail.date)).format("DD/MM/YYYY")}` : ""} (${detailRows.length})`}
          columns={userListColumns(detail.userColumns || userColumns)}
          data={detailRows.slice((page - 1) * pageSize, page * pageSize)}
          loading={detail.loading}
          rowKey={(r, i) => r?._id || `${r?.mobileNo}-${i}`}
          enableSerialNumber
          serialHeader="S.No"
          filters={
            lf && (
              <div style={{ minWidth: 200 }}>
                <CustomDropdowns
                  placeholder={lf.label}
                  options={typeof lf.options === "function" ? lf.options() : lf.options}
                  value={detail.filterValue}
                  onChange={(v) => {
                    setDetail((d) => ({ ...d, filterValue: v || "", loading: Boolean(lf.refetch) }));
                    setPage(1);
                    if (lf.refetch) {
                      // server-side filter: ask again with the picked option
                      Promise.resolve(lf.refetch(detail.row, v || "", dispatch))
                        .then((list) => setDetail((d) => d && { ...d, rows: list || [], loading: false }))
                        .catch(() => setDetail((d) => d && { ...d, loading: false }));
                    }
                  }}
                />
              </div>
            )
          }
          pagination={{
            page,
            pageSize,
            total: detailRows.length,
            onPageChange: setPage,
            onPageSizeChange: (size) => {
              setPageSize(size);
              setPage(1);
            },
          }}
          emptyTitle="No users"
          maxHeight="83vh"
        />
      ) : (
        <div className="d-flex flex-column gap-3">
          <CustomSummaryCards groups={groups} loading={loading} />
          <CustomAdminTable
            enableSerialNumber
            serialHeader="S.No"
            key="days"
            id={tableId}
            title={
              <TableTitle
                title={`Day wise (${rows.length})${filters.section ? ` · ${filters.section.name}${filters.subSection ? ` / ${filters.subSection.name}` : ""}` : ""}`}
                from={filters.startDate}
                to={filters.endDate}
              />
            }
            columns={columns}
            data={tableRows}
            loading={loading}
            rowKey={(r, i) => (r._total ? "total" : r?.[dateKey] || i)}
            rowClassName={(r) => (r._total ? "cat-rowGrandTotal" : "")}
            exportButton={rows.length > 0}
            onExport={onExport}
            advancedFilters={filterFields}
            advancedFilterValues={filters}
            onApplyFilters={(values) => {
              setFilters(values);
              fetchRows(values);
            }}
            applyFiltersOnReset
            onFilterChange={onFilterChange}
            filterSubtitle={sectionKeys ? "Pick the dates, optionally a section" : "Pick the dates"}
            emptyTitle="No records"
            emptyDescription={`Nothing for ${formatDateRange(filters.startDate, filters.endDate)}.`}
            maxHeight="70vh"
          />
        </div>
      )}
    </CustomPageLayout>
  );
};

/** wraps a (body, cb, errCb) action as a Promise of res.data — for column.fetchList */
export const fetchUsers = (dispatch, action, body) =>
  new Promise((resolve, reject) => {
    dispatch(action(body, (res) => resolve(res?.data || []), () => reject(new Error("failed"))));
  });
export const dayOf = (v) => moment.utc(v).format("YYYY-MM-DD");

/* =====================================================================
 * StudentTypePage — Student 2nd / 3rd Analysis (studentType2.js / studentType3.js).
 * Same columns; every count opens the users for that day + payment type (same request as before).
 * ===================================================================== */

const orderLines = (key, fmt = (v) => v) => (_, r) =>
  (r?.orders || []).length ? r.orders.map((o, i) => <div key={i}>{fmt(o?.[key]) || "—"}</div>) : "—";
const STUDENT_USERS = [
  "firstName",
  "lastName",
  { key: "examTypeId", header: "Exam Name", minWidth: 120, render: (v) => v?.name || "—" },
  { key: "baseCourseId", header: "Exam Stream", minWidth: 130, render: (v) => <BatchNames names={v} /> },
  "mobileNo",
  "source",
];
const STUDENT_PAID_USERS = [
  ...STUDENT_USERS,
  { key: "orderNo", header: "Order No", minWidth: 110, render: orderLines("orderNo") },
  { key: "orderDate", header: "Order Date", minWidth: 150, render: orderLines("date", (d) => (d ? moment(d).format("DD/MM/YYYY hh:mm A") : "")) },
  { key: "couponName", header: "Coupon Name", minWidth: 120, render: orderLines("couponName") },
  "whatsapp",
];

/**
 * Props: pageTitle, tableId, ordinal ("2nd" | "3rd"), analysisAction, usersAction
 */
export const StudentTypePage = ({ pageTitle, tableId, ordinal, analysisAction, usersAction }) => {
  const usersKey = `usersForStudent${ordinal}`;
  const s = (key) => (_, summary) => summary?.[key];
  const users = (paymentType, title, paid = false) => ({
    fetchList: (row, _f, dispatch) => fetchUsers(dispatch, usersAction, { date: row?.registrationDate, paymentType }),
    listTitle: title,
    userColumns: paid ? STUDENT_PAID_USERS : [...STUDENT_USERS, "whatsapp"],
  });

  return (
    <OrderDayPage
      pageTitle={pageTitle}
      tableId={tableId}
      exportName="OrderAnalysis" // same file name as before
      fetchAction={analysisAction} // same request as before
      parse={(res) => ({ rows: res?.data?.dayWiseData, summary: res?.data })}
      dateKey="registrationDate"
      columns={[
        { key: usersKey, header: `Users For Student ${ordinal}`, kind: "count", total: s(`totalUsersForStudent${ordinal}`), minWidth: 130 },
        { key: "paidUsers", header: "Paid Users", kind: "count", total: s("totalPaidUsers"), ...users("paid", "Paid Users", true) },
        { key: "registration", header: "Registration", kind: "count", total: s("totalRegisteredUsers") },
        { key: "dayConversion", header: "Day Conversion", kind: "pct", total: (_, sum) => (Number(sum?.totalConversion) || 0).toFixed(2) },
        { key: "activeUsers", header: "Active Users", kind: "count" },
        { key: "inactiveUsers", header: "InActive Users", kind: "count" },
        { key: "totalNotGeneratedRecords", header: "Not Generated Records", kind: "count", ...users("not-generated", "Not Generated Users") },
        // the old Total row summed fields the rows don't have (totalMessageSent / totalMessageRecords) → always 0
        { key: "totalSentMessages", header: "Message Sent", kind: "count", ...users("sent", "Message Sent Users") },
        { key: "totalDeliveredMessages", header: "Message Delivered", kind: "count", ...users("delivered", "Message Delivered Users") },
        { key: "totalReadMessages", header: "Message Read", kind: "count", ...users("read", "Message Read Users") },
        { key: "totalFailedMessages", header: "Failed", kind: "count", ...users("failed", "Failed Message Users") },
        { key: "default", header: "Default", kind: "count", total: s("totalDefaultPayments"), ...users("default", "Default Users") },
        { key: "defaultConversion", header: "Default Conversion", kind: "pct", total: (_, sum) => (Number(sum?.totalDefaultConversion) || 0).toFixed(2) },
        { key: "counsellor", header: "Counsellor", kind: "count", total: s("totalCounsellorPayments"), ...users("counsellor", "Counsellor Users") },
        { key: "counsellorConversion", header: "Counsellor Conversion", kind: "pct", total: (_, sum) => (Number(sum?.totalCounsellorConversion) || 0).toFixed(2) },
        { key: "publication", header: "Publication", kind: "count", total: s("totalPublicationPayments"), ...users("publication", "Publication Users") },
        { key: "publicationConversion", header: "Publication Conversion", kind: "pct", total: (_, sum) => (Number(sum?.totalPublicationConversion) || 0).toFixed(2) },
        { key: "others", header: "Others", kind: "count", total: s("totalOthersPayments"), ...users("others", "Other Users") },
        { key: "othersConversion", header: "Others Conversion", kind: "pct", total: (_, sum) => (Number(sum?.totalOthersConversion) || 0).toFixed(2) },
      ]}
      summaryGroups={(sum) => [
        {
          title: "Total",
          tone: "primary",
          metrics: [
            { label: `Student ${ordinal}`, value: countOf(sum[`totalUsersForStudent${ordinal}`]) },
            { label: "Registration", value: countOf(sum.totalRegisteredUsers) },
            { label: "Paid Users", value: countOf(sum.totalPaidUsers) },
            { label: "Day Conversion", value: pctOf(sum.totalConversion) },
          ],
        },
        paymentGroup("Default", "pink", sum.totalDefaultPayments, sum.totalDefaultConversion),
        paymentGroup("Counsellor", "warning", sum.totalCounsellorPayments, sum.totalCounsellorConversion),
        paymentGroup("Publication", "success", sum.totalPublicationPayments, sum.totalPublicationConversion),
        paymentGroup("Others", "info", sum.totalOthersPayments, sum.totalOthersConversion),
      ]}
    />
  );
};
